import { useQuery } from "@tanstack/react-query";
import React from "react";
import Tooltip from "@mui/material/Tooltip";
import Loader from "../../../components/elements/Loader";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { ClearanceStatus } from "../../../utils/constants";
import { calculateNightsCount } from "../../../utils/Utils";
import infoIcon from "../../../assets/images/info_icon.svg";
import "../css/PaymentInformation.css";

const PaymentInformation = ({
  tripId,
  overtimeStatus,
  escortStatus,
  hasBothTerminals,
  tripStatus,
  arrivalTime,
  departureTimeFromArrivalAirport,
  mtow,
}) => {
  const { getTripFee } = useLandingAuthApi();
  const { data: paymentsDetails, isPaymentLoading } = useQuery({
    queryKey: ["tripFee", tripId],
    queryFn: () => getTripFee(tripId),
  });

  const payment = paymentsDetails?.data;

  if (isPaymentLoading) {
    return <Loader data-testid="loader" />;
  }

  const calculateVAT = (amount) => {
    return amount * 0.1;
  };

  const escortFeeTotal =
    payment?.arrivalEscortOfficerFee +
    payment?.arrivalEscortVehicleFee +
    payment?.departureEscortOfficerFee +
    payment?.departureEscortScreeningFee +
    payment?.departureEscortVehicleFee;

  const overTimeTotal =
    payment?.arrivalOvertimeFee + payment?.departureOvertimeFee;

  const paidEscortFee =
    escortStatus === ClearanceStatus.PAID
      ? tripStatus === "CANCELLED"
        ? 0
        : escortFeeTotal
      : 0;
  const pendingEscortFee =
    escortStatus === ClearanceStatus.PAID ||
      escortStatus === ClearanceStatus.CANCELLED
      ? 0
      : tripStatus === "CANCELLED"
        ? 0
        : escortFeeTotal;

  const paidOvertimeFee =
    overtimeStatus === ClearanceStatus.PAID
      ? tripStatus === "CANCELLED"
        ? 0
        : overTimeTotal
      : 0;
  const pendingOvertimeFee =
    overtimeStatus === ClearanceStatus.PAID ||
      overtimeStatus === ClearanceStatus.CANCELLED
      ? 0
      : tripStatus === "CANCELLED"
        ? 0
        : overTimeTotal;

  const subTotal =
    payment?.securityFee +
    payment?.landingFee +
    payment?.parkingFee +
    paidEscortFee +
    paidOvertimeFee;

  const mainFeeTotal = subTotal + calculateVAT(subTotal);

  const additionalSubFeeTotal = pendingEscortFee + pendingOvertimeFee;
  const additionalFeeTotal =
    additionalSubFeeTotal + calculateVAT(additionalSubFeeTotal);

  const renderFeeItem = (label, amount, status) => {
    if (amount > 0) {
      return (
        <div className={`fee-item ${status}`}>
          <p>
            {label}
            {status != null && (
              <span>
                {" "}
                (
                {tripStatus === "CANCELLED" && status.toUpperCase() === "PAID"
                  ? "REFUNDED"
                  : status.toUpperCase()}
                )
              </span>
            )}
          </p>
          <div className="fee-amount">$ {amount}</div>
        </div>
      );
    }
    return null;
  };
  const renderTotalItem = (label, amount, status) => {
    if (amount > 0) {
      return (
        <div className={`fee-item ${status}`}>
          <p>
            {label}
            {status != null && <span> ( {status} )</span>}
          </p>
          <div className="fee-amount">BSD {amount}</div>
        </div>
      );
    }
    return null;
  };

  const formatAmount = (amount) => {
    return Number(amount).toFixed(2);
  };

  const paymentStatus =
    Number(formatAmount(mainFeeTotal)) <= Number(formatAmount(payment?.feePaid))
      ? tripStatus === "CANCELLED"
        ? "Refunded"
        : "Paid"
      : "Pending";

  const nightsCount = calculateNightsCount(
    arrivalTime,
    departureTimeFromArrivalAirport
  );

  return (
    payment?.totalFee > 0 && (
      <div>
        <div className="payment-information" data-testid="initial-payment-information">
          <p>Payment Information</p>
          {hasBothTerminals ? (
            <div className="payment-item">
              <div className="payment-info-row">
                <p className="payment-amount">
                  No approval necessary for this case in Landing Authorization.
                </p>
              </div>
            </div>
          ) : (
            <>
              {mainFeeTotal > 0 && (
                <div className="payment-item" data-testid="initial-payment-information-item">
                  <div className="payment-info-row">
                    <p
                      className={`payment-amount ${paymentStatus.toLowerCase()}`}
                    >
                      BSD {formatAmount(mainFeeTotal)}{" "}
                      <span>({paymentStatus.toUpperCase()})</span>
                    </p>
                  </div>

                  <div className="fees">
                    {payment?.securityFee > 0 &&
                      renderFeeItem(
                        "Security Fees",
                        formatAmount(payment?.securityFee)
                      )}
                    {payment?.landingFee > 0 &&
                      renderFeeItem(
                        "Landing Fees",
                        formatAmount(payment?.landingFee)
                      )}
                    {payment?.parkingFee > 0 && (
                      <div className={`fee-item`}>
                        <p>
                          Parking Fees
                          <Tooltip
                            title={
                              <div className="payment-tooltip-content">
                                <div>MTOW: {mtow ?? "-"} lbs</div>
                                <div>No. of Nights: {nightsCount}</div>
                                <div>
                                  Total: BSD {formatAmount(payment?.parkingFee)}
                                </div>
                              </div>
                            }
                            arrow
                          >
                            <img
                              src={infoIcon}
                              alt="info"
                              className="payment-info-icon"
                            />
                          </Tooltip>
                        </p>
                        <div className="fee-amount">
                          $ {formatAmount(payment?.parkingFee)}
                        </div>
                      </div>
                    )}
                    {paidEscortFee > 0 &&
                      renderFeeItem(
                        "Special Onboarding Fees",
                        formatAmount(paidEscortFee)
                      )}
                    {paidOvertimeFee > 0 &&
                      renderFeeItem(
                        "After Hours Fees",
                        formatAmount(paidOvertimeFee)
                      )}
                  </div>

                  {subTotal > 0 && (
                    <div className="total-fees">
                      <>
                        {renderTotalItem("Sub Total", formatAmount(subTotal))}
                        {renderTotalItem(
                          "VAT (10%)",
                          formatAmount(calculateVAT(subTotal))
                        )}
                      </>
                    </div>
                  )}

                  {mainFeeTotal > 0 && (
                    <div className="total-fees">
                      {renderTotalItem("Total", formatAmount(mainFeeTotal))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {!hasBothTerminals && additionalFeeTotal > 0 && (
          <div className="payment-information" data-testid="additional-payment-information">
            <div className="payment-item">
              <div className="payment-info-row">
                <p className="payment-amount pending">
                  BSD {formatAmount(additionalFeeTotal)} <span>(PENDING)</span>
                </p>
              </div>
              <div className="fees">
                {pendingEscortFee > 0 &&
                  renderFeeItem(
                    "Special Onboarding Fees",
                    formatAmount(pendingEscortFee)
                  )}
                {pendingOvertimeFee > 0 &&
                  renderFeeItem(
                    "After Hours Fees",
                    formatAmount(pendingOvertimeFee)
                  )}
              </div>
              <div className="total-fees">
                {renderTotalItem(
                  "Sub Total",
                  formatAmount(additionalSubFeeTotal)
                )}
                {renderTotalItem(
                  "VAT (10%)",
                  formatAmount(calculateVAT(additionalSubFeeTotal))
                )}
              </div>
              <div className="total-fees" data-testid="total-fees">
                {renderTotalItem("Total", formatAmount(additionalFeeTotal))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default PaymentInformation;
